import { useState, useEffect, useMemo, useRef } from 'react';
import { useReportData } from '../../hooks/useReportData';
import type { ReportParams } from '../../types/widget';

interface ColumnDef {
    key: string;
    label: string;
    align?: 'left' | 'right' | 'center';
    format?: (value: unknown, row: Record<string, unknown>) => string;
    filterable?: boolean;
    /** Clé à lire dans meta.totals pour afficher la ligne de pied de tableau. */
    totalKey?: string;
    /** Formatter dédié au footer (sinon fallback sur `format`). */
    formatTotal?: (value: number) => string;
}

interface ReportDrillDownProps {
    title: string;
    endpoint: string;
    params: ReportParams;
    columns: ColumnDef[];
    enabled: boolean;
    onClose: () => void;
    perPage?: number;
    /* Navigation entre tranches */
    tranches?: string[];
    currentTranche?: string;
    onTrancheChange?: (tranche: string) => void;
}

const PER_PAGE_OPTIONS = [10, 30, 50, 100] as const;
const LS_KEY = 'drilldown_per_page';

function readPerPage(): number {
    try {
        const v = Number(localStorage.getItem(LS_KEY));
        return PER_PAGE_OPTIONS.includes(v as any) ? v : 30;
    } catch { return 30; }
}

function formatCell(value: unknown, col: ColumnDef, row: Record<string, unknown>): string {
    if (value === null || value === undefined) return '—';
    if (col.format) return col.format(value, row);
    if (typeof value === 'number') return value.toLocaleString('fr-FR');
    return String(value);
}

export function ReportDrillDown({
    title, endpoint, params, columns, enabled, onClose,
    perPage: perPageProp,
    tranches, currentTranche, onTrancheChange,
}: ReportDrillDownProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(() => perPageProp ?? readPerPage());
    const [sortKey, setSortKey] = useState<string | null>(null);
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
    const hasScrolled = useRef(false);
    const prevDataRef = useRef<Record<string, unknown>[]>([]);
    const prevTotalsRef = useRef<Record<string, number> | null>(null);
    const [colFilters, setColFilters] = useState<Record<string, string>>({});
    const [debouncedFilters, setDebouncedFilters] = useState<Record<string, string>>({});

    // Debounce column filters (300ms)
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedFilters(colFilters);
            setPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [colFilters]);

    // Reset sort when endpoint/base params change (exclude page/perPage/sort/filters)
    const resetKey = useMemo(() => {
        const base: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(params)) {
            if (!['page', 'per_page', 'sort_by', 'sort_direction'].includes(k) && !k.startsWith('filter_')) {
                base[k] = v;
            }
        }
        return JSON.stringify({ endpoint, params: base });
    }, [endpoint, params]);
    useEffect(() => { setSortKey(null); setSortDir('asc'); setColFilters({}); }, [resetKey]);

    // Build filter_* params from debounced column filters
    const filterParams = useMemo(() => {
        const fp: Record<string, string> = {};
        for (const [key, val] of Object.entries(debouncedFilters)) {
            if (val) fp[`filter_${key}`] = val;
        }
        return fp;
    }, [debouncedFilters]);

    const paginatedParams = {
        ...params,
        page,
        per_page: perPage,
        ...(sortKey ? { sort_by: sortKey, sort_direction: sortDir } : {}),
        ...filterParams,
    };
    const { data, loading, error, meta } = useReportData(endpoint, paginatedParams, enabled);

    const total = (meta?.total as number) ?? data.length;
    const totalPages = Math.ceil(total / perPage);
    const totals = (meta?.totals as Record<string, number> | undefined) ?? undefined;
    const hasTotalsConfig = useMemo(() => columns.some((c) => c.totalKey), [columns]);

    // Keep previous data/totals for stable height during loading
    useEffect(() => {
        if (!loading && data.length > 0) {
            prevDataRef.current = data as Record<string, unknown>[];
        }
        if (!loading && totals) {
            prevTotalsRef.current = totals;
        }
    }, [loading, data, totals]);

    // Scroll into view on first render
    useEffect(() => {
        if (enabled && !hasScrolled.current) {
            hasScrolled.current = true;
            setTimeout(() => containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
        }
        if (!enabled) hasScrolled.current = false;
    }, [enabled]);

    if (!enabled) return null;

    const handleSort = (key: string) => {
        if (sortKey === key) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
        setPage(1);
    };

    const handlePerPageChange = (value: number) => {
        setPerPage(value);
        setPage(1);
        try { localStorage.setItem(LS_KEY, String(value)); } catch {}
    };

    // Column filter UI
    const filterableKeys = useMemo(() => new Set(columns.filter((c) => c.filterable === true).map((c) => c.key)), [columns]);
    const hasFilterableColumns = filterableKeys.size > 0;

    // Tranche navigation
    const trancheIdx = tranches && currentTranche ? tranches.indexOf(currentTranche) : -1;

    return (
        <div ref={containerRef} className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-b border-gray-200 gap-2">
                <h4 className="text-sm font-medium text-gray-700 truncate">{title}</h4>

                {tranches && tranches.length > 1 && onTrancheChange && (
                    <div className="flex items-center gap-1 shrink-0">
                        <button
                            onClick={() => onTrancheChange(tranches[trancheIdx - 1])}
                            disabled={trancheIdx <= 0}
                            className="px-2 py-1 text-sm rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-100"
                        >
                            &#9664; Préc.
                        </button>
                        <select
                            value={currentTranche ?? ''}
                            onChange={(e) => onTrancheChange(e.target.value)}
                            className="text-sm border border-gray-300 rounded px-2 py-1"
                        >
                            {tranches.map((t) => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                        <button
                            onClick={() => onTrancheChange(tranches[trancheIdx + 1])}
                            disabled={trancheIdx < 0 || trancheIdx >= tranches.length - 1}
                            className="px-2 py-1 text-sm rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-100"
                        >
                            Suiv. &#9654;
                        </button>
                    </div>
                )}

                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none shrink-0">&times;</button>
            </div>

            {error && (
                <div className="px-4 py-3 bg-red-50 text-red-700 text-sm">{error}</div>
            )}

            {!!meta?.message && (
                <div className="px-4 py-3 bg-yellow-50 text-yellow-700 text-sm">{meta.message as string}</div>
            )}

            <div className="overflow-x-auto relative">
                {loading && prevDataRef.current.length > 0 && (
                    <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
                        <span className="text-sm text-gray-400">Chargement...</span>
                    </div>
                )}
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                            {columns.map((col) => (
                                <th
                                    key={col.key}
                                    className={`px-3 py-2 font-medium text-gray-600 text-${col.align ?? 'left'} cursor-pointer select-none hover:text-gray-900`}
                                    onClick={() => handleSort(col.key)}
                                >
                                    {col.label}
                                    {sortKey === col.key && (
                                        <span className="ml-1">{sortDir === 'asc' ? '▲' : '▼'}</span>
                                    )}
                                </th>
                            ))}
                        </tr>
                        {hasFilterableColumns && (
                            <tr className="border-b border-gray-200">
                                {columns.map((col) => (
                                    <th key={col.key} className="px-2 py-1">
                                        {filterableKeys.has(col.key) ? (
                                            <input
                                                type="text"
                                                value={colFilters[col.key] ?? ''}
                                                onChange={(e) => setColFilters((f) => ({ ...f, [col.key]: e.target.value }))}
                                                placeholder="Filtrer…"
                                                className="w-full text-xs font-normal border border-gray-200 rounded px-2 py-1 focus:outline-none focus:border-brand-400"
                                            />
                                        ) : null}
                                    </th>
                                ))}
                            </tr>
                        )}
                    </thead>
                    <tbody>
                        {loading && prevDataRef.current.length === 0 && (
                            <tr>
                                <td colSpan={columns.length} className="px-3 py-8 text-center text-gray-400">
                                    Chargement...
                                </td>
                            </tr>
                        )}
                        {!loading && data.length === 0 && (
                            <tr>
                                <td colSpan={columns.length} className="px-3 py-8 text-center text-gray-400">
                                    Aucune donnée
                                </td>
                            </tr>
                        )}
                        {(loading ? prevDataRef.current : data).map((row, i) => (
                            <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                                {columns.map((col) => (
                                    <td
                                        key={col.key}
                                        className={`px-3 py-2 text-${col.align ?? 'left'}`}
                                    >
                                        {formatCell((row as Record<string, unknown>)[col.key], col, row as Record<string, unknown>)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                    {hasTotalsConfig && (() => {
                        const displayedTotals = totals ?? prevTotalsRef.current;
                        if (!displayedTotals) return null;
                        const totalLabel = 'Total';
                        let labelPlaced = false;
                        return (
                            <tfoot>
                                <tr className="bg-gray-100 border-t-2 border-gray-300 font-medium">
                                    {columns.map((col) => {
                                        const raw = col.totalKey ? displayedTotals[col.totalKey] : undefined;
                                        const hasTotal = col.totalKey && raw != null;
                                        let content: string | null = null;
                                        if (hasTotal) {
                                            content = col.formatTotal
                                                ? col.formatTotal(raw as number)
                                                : col.format
                                                    ? col.format(raw, {})
                                                    : (raw as number).toLocaleString('fr-FR');
                                        } else if (!labelPlaced) {
                                            content = totalLabel;
                                            labelPlaced = true;
                                        }
                                        return (
                                            <td
                                                key={col.key}
                                                className={`px-3 py-2 text-${col.align ?? 'left'}`}
                                            >
                                                {content}
                                            </td>
                                        );
                                    })}
                                </tr>
                            </tfoot>
                        );
                    })()}
                </table>
            </div>

            {(totalPages > 1 || true) && (
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-sm">
                    <div className="flex items-center gap-3">
                        <span className="text-gray-500">
                            {total} résultat{total > 1 ? 's' : ''}
                            {totalPages > 1 && <> — page {page}/{totalPages}</>}
                        </span>
                        <select
                            value={perPage}
                            onChange={(e) => handlePerPageChange(Number(e.target.value))}
                            className="text-sm border border-gray-300 rounded px-2 py-1"
                        >
                            {PER_PAGE_OPTIONS.map((n) => (
                                <option key={n} value={n}>{n} / page</option>
                            ))}
                        </select>
                    </div>
                    {totalPages > 1 && (
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page <= 1}
                                className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-100"
                            >
                                Précédent
                            </button>
                            <button
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={page >= totalPages}
                                className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-100"
                            >
                                Suivant
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
